<template>
  <CustomModal
    :is-modal-open="isOpen"
    @toggleModal="resetModal"
    @onClickAway="resetModal"
    :overlay="'dark'"
    :with-header="true"
    :with-footer="true"
    custom-classes="tw:h-screen tw:md:h-auto tw:w-[100vw] tw:md:max-w-[588px] tw:md:w-[588px] tw:md:rounded-md tw:rounded-none"
  >
    <template #modalHeader>
      <div class="tw:flex tw:items-center">
        <TriangleWarningColors
          v-if="step === 1"
          class="tw:fill-none tw:mr-4 tw:stroke-gray-800 tw:h-[25px] tw:w-[24px]"
        />

        <CheckMarkInCircle
          v-if="step === 2 && isPositive"
          height="24px"
          width="24px"
          class="tw:inline tw:mr-4 tw:stroke-gray-700"
        />
        <CloseOutline
          v-if="step === 2 && !isPositive"
          height="24px"
          width="24px"
          class="tw:inline tw:mr-4 tw:stroke-gray-700"
        />
        <h3 class="tw:font-bold tw:text-xl tw:text-gray-700">{{ $t(titleLabel) }}</h3>
      </div>
    </template>

    <div class="tw:flex tw:flex-col tw:w-full">
      <PauseFlowCPCStep1
        v-if="step === 0"
        :reasons="reasons"
        :selectedReasonObject="selectedReasonObj"
        :selectedReason="selectedReason"
        :showHint="hintsVisible"
        :isItemNetCPAEligible="isNetCpaEligible"
        :otherExplanation="otherExplanation"
        @updateOtherExplanation="updateOtherExplanation"
        @selectReason="selectReason"
        @toggleChangeCampaignModal="toggleChangeCampaignModal"
      />

      <PauseFlowCPCStep2 v-if="step === 1" :campaignType="currentCampaignType" />

      <PauseFlowCPCStep3
        v-if="step === 2"
        :isPositive="isPositive"
        :retries="retries"
        :feedback="feedback"
        @setFeedback="setFeedback"
        :reasonId="selectedReasonObj?.id"
      />
    </div>

    <template #modalFooter>
      <div class="tw:flex tw:justify-end tw:w-full">
        <GhostButton @click="closePopup" :disabled="isLoading">
          {{ $t(cancelButtonLabel) }}
        </GhostButton>
        <DangerButton v-show="step === 1" @click="nextStep" class="tw:ml-6" :disabled="isNextActionDisabled">
          <span v-show="!isLoading">{{ $t(nextButtonLabel) }}</span>
          <span v-show="isLoading" class="tw:px-4"><SmallDotLoader color="#fff" /></span>
        </DangerButton>
        <MainButton v-show="step !== 1" @click="nextStep" class="tw:ml-6" :disabled="isNextActionDisabled">
          <span v-show="!isLoading">{{ $t(nextButtonLabel) }}</span>
          <span v-show="isLoading" class="tw:px-4"><SmallDotLoader color="#fff" /></span>
        </MainButton>
      </div>
    </template>
  </CustomModal>
</template>

<script setup>
import { ref, computed } from 'vue'

// Component imports
import CustomModal from '@/components/base/CustomModal.vue'
import CheckMarkInCircle from '@/components/icons/CheckMarkInCircle.svg?component'
import TriangleWarningColors from '@/components/icons/TriangleWarningColors.svg?component'
import CloseOutline from '@/components/icons/CloseOutline.svg?component'
import GhostButton from '@/components/base/buttons/GhostButton.vue'
import MainButton from '@/components/base/buttons/MainButton.vue'
import DangerButton from '@/components/base/buttons/DangerButton.vue'
import SmallDotLoader from '@/components/base/loaders/SmallDotLoader.vue'
import PauseFlowCPCStep1 from '@/components/rateConnect/pauseFlow/cpc/PauseFlowCPCStep1.vue'
import PauseFlowCPCStep2 from '@/components/rateConnect/pauseFlow/cpc/PauseFlowCPCStep2.vue'
import PauseFlowCPCStep3 from '@/components/rateConnect/pauseFlow/cpc/PauseFlowCPCStep3.vue'

// Utilities and composables
import useToastNotifications from '@/components/notifications/useToastNotifications'
import appAnalytics from '@/utils/tracking'

import { reasons } from '@/constants/rcPauseReasons.js'
import { useCampaign, useCampaignMutation, usePartners } from '@/components/rateConnect/queries'
import { selectedAccommodationId, useUserDetails } from '@/layouts/queries'
import { getContactUrl } from '@/utils/Utility.ts'
import { useRateConnect } from '@/components/rateConnect/useRateConnect'
import { useSessionValues } from '@/composables/useSessionValues'

// ============================================================================
// PROPS & EMITS
// ============================================================================
defineProps({
  isOpen: Boolean,
})

const emit = defineEmits(['toggleChangeCampaignModal'])

// ============================================================================
// COMPOSABLES & EXTERNAL DATA
// ============================================================================
const { displayNotification, showSaveError } = useToastNotifications()
const { currentCampaignType } = useCampaign(selectedAccommodationId)
const { isNetCpaEligible } = usePartners(selectedAccommodationId)
const { deactivateCampaign } = useCampaignMutation()
const { togglePauseCampaign } = useRateConnect()

const { userId, language } = useSessionValues()
const { userDetails } = useUserDetails(userId)

// ============================================================================
// STATE (REFS)
// ============================================================================
const step = ref(0)
const hintsVisible = ref(false)
const selectedReason = ref(null)
const selectedReasonObj = ref(null)
const otherExplanation = ref('')
const feedback = ref('')
const isPositive = ref(null)
const isLoading = ref(false)
const retries = ref(0)

// ============================================================================
// COMPUTED PROPERTIES
// ============================================================================
const isNextActionDisabled = computed(() => {
  if (
    (step.value === 0 && selectedReason.value === null) ||
    (step.value === 0 && hintsVisible.value && selectedReasonObj.value?.id === 11 && otherExplanation.value === '') ||
    (step.value === 2 && isPositive.value && feedback.value === '') ||
    isLoading.value
  )
    return true
  else return false
})

const titleLabel = computed(() => {
  if (step.value === 0) return 'rc_pause_flow_pause_header'
  if (step.value === 1) return 'rc_pause_flow_reminder_header'
  if (step.value === 2 && isPositive.value) return 'rc_pause_flow_paused_header'
  if (step.value === 2 && !isPositive.value) return 'rc_pause_flow_pause_error_header'
  return ''
})

const cancelButtonLabel = computed(() => {
  if (step.value === 0 || step.value === 1) return 'rc_pause_flow_keep_rates_cta'
  if (step.value === 2 && isPositive.value) return 'rc_pause_flow_feedback_no_cta'
  if (step.value === 2 && !isPositive.value) return 'close'
  return 'close'
})

const nextButtonLabel = computed(() => {
  if (step.value === 0) return 'rc_pause_flow_continue'
  if (step.value === 1) return 'rc_deactivate_campaign_button'
  if (step.value === 2 && isPositive.value) return 'rc_pause_flow_feedback_send_cta'
  if (step.value === 2 && !isPositive.value && retries.value <= 2) return 'cancellation_fail_retry_cta'
  if (step.value === 2 && !isPositive.value && retries.value > 2) return 'Cancellation_fail_support_cta'
  return ''
})

const supportCenterUrl = computed(() =>
  getContactUrl(language.value, userId.value, selectedAccommodationId.value, true)
)

// ============================================================================
// FUNCTIONS
// ============================================================================
const selectReason = reason => {
  hintsVisible.value = true
  const reasonGroup = reason.split('-')[0].trim()
  const reasonId = Number(reason.split('-')[1].trim())
  selectedReason.value = reason
  reasons.forEach(reason => {
    if (reason.group.value === reasonGroup) {
      selectedReasonObj.value = reason.options.find(item => item.id === reasonId)
    }
  })
  appAnalytics.track(appAnalytics.events.RC_PAUSE_CAMPAIGN_REASON_SELECTED, {
    reason: selectedReasonObj.value?.value,
    accommodationId: selectedAccommodationId.value,
    campaignType: currentCampaignType.value,
    isNetCPAEligible: isNetCpaEligible.value,
  })
}

const toggleChangeCampaignModal = () => {
  appAnalytics.track(appAnalytics.events.RC_PAUSE_CAMPAIGN_CHANGE_CAMPAIGN_TYPE, {
    reason: selectedReasonObj.value?.value,
    accommodationId: selectedAccommodationId.value,
    campaignType: currentCampaignType.value,
    isNetCPAEligible: isNetCpaEligible.value,
  })
  emit('toggleChangeCampaignModal', 'pauseFlow')
  resetModal()
}

const setFeedback = text => {
  feedback.value = text
}

const updateOtherExplanation = text => {
  otherExplanation.value = text
}

const nextStep = () => {
  if (step.value === 0 && hintsVisible.value) {
    appAnalytics.track(appAnalytics.events.RC_PAUSE_CAMPAIGN_CONTINUE_BUTTON_CLICKED, {
      reason: selectedReasonObj.value?.value,
      accommodationId: selectedAccommodationId.value,
      campaignType: 'cpc',
      isNetCPAEligible: isNetCpaEligible.value,
    })
    step.value = 1
  } else if (step.value === 1) {
    onDeactivateCampaign()
  } else if (step.value === 2 && !isPositive.value && retries.value <= 2) {
    retries.value += 1
    onDeactivateCampaign()
  } else if (step.value === 2 && !isPositive.value && retries.value > 2) {
    redirectToContactUs()
  } else if (step.value === 2 && isPositive.value) {
    appAnalytics.track(appAnalytics.events.RC_PAUSE_CAMPAIGN_SEND_FEEDBACK, {
      reason: selectedReasonObj.value?.value,
      accommodationId: selectedAccommodationId.value,
      campaignType: 'cpc',
      isNetCPAEligible: isNetCpaEligible.value,
      feedback: feedback.value,
    })
    displayNotification({
      title: 'setting_updated_success',
      titleTranslationKey: true,
      message: 'rc_deactivate_campaign_success_message',
      isTranslationKey: true,
      type: 'info',
    })
    resetModal()
  }
}

const closePopup = () => {
  if (step.value === 0 && hintsVisible.value) {
    appAnalytics.track(appAnalytics.events.RC_PAUSE_CAMPAIGN_2_KEEP_RATES_LIVE, {
      reason: selectedReasonObj.value?.value,
      accommodationId: selectedAccommodationId.value,
      campaignType: 'cpc',
      isNetCPAEligible: isNetCpaEligible.value,
    })
  } else if (step.value === 1) {
    appAnalytics.track(appAnalytics.events.RC_PAUSE_CAMPAIGN_3_KEEP_RATES_LIVE, {
      reason: selectedReasonObj.value?.value,
      accommodationId: selectedAccommodationId.value,
      campaignType: 'cpc',
      isNetCPAEligible: isNetCpaEligible.value,
    })
  } else if (step.value === 2 && isPositive.value) {
    appAnalytics.track(appAnalytics.events.RC_PAUSE_CAMPAIGN_NO_FEEDBACK, {
      reason: selectedReasonObj.value?.value,
      accommodationId: selectedAccommodationId.value,
      campaignType: 'cpc',
      isNetCPAEligible: isNetCpaEligible.value,
    })
    displayNotification({
      title: 'setting_updated_success',
      titleTranslationKey: true,
      message: 'rc_deactivate_campaign_success_message',
      isTranslationKey: true,
      type: 'info',
    })
  }
  resetModal()
}

const resetModal = () => {
  selectedReason.value = null
  selectedReasonObj.value = null
  step.value = 0
  hintsVisible.value = false
  otherExplanation.value = ''
  isPositive.value = null
  retries.value = 0
  feedback.value = ''

  togglePauseCampaign('close', false)
}

const redirectToContactUs = () => {
  resetModal()
  return window.open(supportCenterUrl.value, '_blank')
}

const onDeactivateCampaign = () => {
  if (!selectedAccommodationId.value) return
  isLoading.value = true
  appAnalytics.track(appAnalytics.events.RC_PAUSE_CAMPAIGN_IN_FLOW_BUTTON_CLICKED, {
    reasonGroup: selectedReasonObj.value?.group,
    reason:
      selectedReasonObj.value?.id === 11
        ? `${selectedReasonObj.value?.value}: ${otherExplanation.value}`
        : selectedReasonObj.value?.value,
    accommodationId: selectedAccommodationId.value,
    campaignType: currentCampaignType.value,
    isNetCPAEligible: isNetCpaEligible.value,
    language: language.value,
    email: userDetails.value?.email,
    firstName: userDetails.value?.firstName,
    lastName: userDetails.value?.lastName,
  })

  deactivateCampaign(
    {
      accommodationId: selectedAccommodationId.value,
    },
    {
      onSuccess: () => {
        isLoading.value = false
        step.value = 2
        isPositive.value = true
      },
      onError: () => {
        isLoading.value = false
        isPositive.value = false
        showSaveError()
      },
    }
  )
}
</script>
